import { toast } from "react-toastify";
import { Notification } from "../components/Notification";

export type Method = "POST" | "GET" | "PUT" | "PATCH" | "DELETE";

interface FetchApiProps {
  endpoint: string;
  method?: Method;
  body?: Record<string, any>;
  JWT?: string;
  contentType?: string;
  format?: "json" | "blob";
  headers?: HeadersInit;
}

export const FetchApi = async ({
  endpoint,
  method = "GET",
  body,
  JWT,
  contentType = "application/json",
  format = "json",
  headers = {}
}: FetchApiProps) => {
  try {
    const response = await fetch(endpoint, {
      method,
      headers: {
        "Content-Type": contentType,
        ...(JWT ? { Authorization: `Bearer ${JWT}` } : {}),
        ...headers,
      },
      body: body && method !== "GET" ? JSON.stringify(body) : undefined,
    });

    let formattedResponse;
    try {
      formattedResponse =
        format === "blob" ? await response.blob() : await response.json();
    } catch {
      toast.error(
        <Notification
          title="Something went wrong"
          description="Unable to parse response"
        />
      );
      return;
    }

    if (!response.ok) {
      handleError(response.status, formattedResponse);
      return response;
    }

    return formattedResponse;
  } catch (error: any) {
    toast.error(
      <Notification
        title="Something went wrong"
        description={error?.message || "Network request failed"}
      />
    );
  }
};

const handleError = (status: number, data: any) => {
  if (data?.errors) {
    data.errors.forEach((err: any) => {
      const description =
        err?.message_list?.join(", ") || `${err?.Field || ""} ${err?.Tag || ""}`;
      toast.error(
        <Notification title={`${err?.name || "Error"}!`} description={description} />
      );
    });
    return;
  }

  if (data?.error) {
    toast.error(
      <Notification title="Error!" description={data.error} />
    );
    return;
  }

  if (status === 500) {
    toast.error(
      <Notification
        title="Server Error"
        description={data?.message || "An unexpected error occurred"}
      />
    );
  } else if (status === 401) {
    localStorage.clear()
    sessionStorage.clear()
    window.location.replace("/");
  } else {
    toast.error(
      <Notification
        title="Something went wrong"
        description="An unknown error occurred"
      />
    );
  }
};
